import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {Map<string, string>} */
  static #sectionCache = new Map();

  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.initSizeSystemSwitchers();
    this.#resizeObserver.observe(this);
    this.preloadVariantImages();
    this.initHoverPreload();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('[data-size-system-switcher]')) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    const isColorVariantImage = Boolean(event.target.closest('.variant-option--variant-images'));
    const mediaId = selectedOption.dataset.mediaId || selectedOption.closest?.('[data-media-id]')?.dataset?.mediaId || '';
    const hasVariantMedia = Boolean(mediaId);

    this.updateSelectedOption(event.target);
    if (hasVariantMedia && !event.detail?.fromSticky) {
      this.scrollToVariantMedia(mediaId, event.target);
    }

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const { optionValueId = '', variantId = '', connectedProductUrl = '' } = selectedOption.dataset;
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      detail: { optionValueId, variantId, connectedProductUrl, mediaId, isColorSelection: isColorVariantImage || hasVariantMedia },
    });

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }

        const legendValue = fieldset?.querySelector('.variant-option__selected-value, .variant-option__swatch-value');
        if (legendValue && target.value) {
          const currentSwitcher = fieldset?.querySelector('[data-size-system-switcher]');
          const currentSystem = (currentSwitcher?.value || localStorage.getItem('shopify_size_system') || 'EU').toLowerCase();
          const targetLabel = target.closest('.variant-option__button-label');
          const localizedVal = targetLabel?.getAttribute(`data-size-${currentSystem}`) || target.getAttribute?.(`data-size-${currentSystem}`);
          legendValue.textContent = localizedVal || target.value;
        }

        const labels = fieldset?.querySelectorAll('.variant-option__button-label');
        labels?.forEach((label) => {
          if (label.contains(target)) {
            label.classList.add('is-selected');
          } else {
            label.classList.remove('is-selected');
          }
        });
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Preload variant responsive images and pre-decode them to GPU memory during idle time,
   * and prefetch variant section HTML in the background for 0ms switching.
   */
  preloadVariantImages() {
    const doPreload = () => {
      const elements = this.querySelectorAll('[data-media-srcset], [data-media-src]');
      const preloadedImages = new Set();

      elements.forEach((el) => {
        const srcset = el.dataset.mediaSrcset;
        const src = el.dataset.mediaSrc;
        const key = srcset || src;
        if (!key || preloadedImages.has(key)) return;
        preloadedImages.add(key);

        const img = new Image();
        if (srcset) {
          img.srcset = srcset;
          img.sizes = '(min-width: 750px) 50vw, 100vw';
        }
        if (src) {
          img.src = src;
        }

        if ('decode' in img) {
          img.decode().catch(() => {});
        }
      });

      // Prefetch section HTML for all variants in the background
      const options = this.querySelectorAll(
        'select.variant-option__select option[data-option-value-id], fieldset input[data-option-value-id]'
      );
      options.forEach((opt) => {
        if (!(opt instanceof HTMLElement)) return;
        try {
          const url = this.buildRequestUrl(opt);
          if (url && !VariantPicker.#sectionCache.has(url)) {
            fetch(url, { priority: 'low' })
              .then((r) => r.text())
              .then((text) => {
                VariantPicker.#sectionCache.set(url, text);
              })
              .catch(() => {});
          }
        } catch (e) {}
      });
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(doPreload, { timeout: 1500 });
    } else {
      setTimeout(doPreload, 200);
    }
  }

  /**
   * Priority decode on hover / touch anticipation before click completes,
   * and prefetch the section HTML into memory cache.
   */
  initHoverPreload() {
    this.addEventListener(
      'pointerenter',
      (event) => {
        const target =
          event.target instanceof HTMLElement
            ? event.target.closest('[data-option-value-id], [data-media-srcset], [data-media-src]')
            : null;
        if (!target) return;

        // 1. Preload and decode image into GPU memory
        const srcset = target.dataset.mediaSrcset;
        const src = target.dataset.mediaSrc;
        if (srcset || src) {
          const img = new Image();
          if (srcset) {
            img.srcset = srcset;
            img.sizes = '(min-width: 750px) 50vw, 100vw';
          }
          if (src) {
            img.src = src;
          }
          if ('decode' in img) {
            img.decode().catch(() => {});
          }
        }

        // 2. Prefetch section HTML on hover so click handler is instant (0ms)
        if (target.dataset.optionValueId) {
          try {
            const url = this.buildRequestUrl(target);
            if (url && !VariantPicker.#sectionCache.has(url)) {
              fetch(url)
                .then((r) => r.text())
                .then((text) => {
                  VariantPicker.#sectionCache.set(url, text);
                })
                .catch(() => {});
            }
          } catch (e) {}
        }
      },
      { capture: true, passive: true }
    );
  }

  /**
   * Smoothly and instantly switches to the variant media on both Desktop Grid and Mobile Slideshow.
   * @param {string | undefined} mediaId - The media ID to scroll to.
   * @param {HTMLElement} [targetElement] - The clicked option/target element.
   */
  scrollToVariantMedia(mediaId, targetElement) {
    if (!mediaId && !targetElement) return;

    const gallery = document.querySelector('media-gallery, .product-media-gallery, .product-information__media');
    if (!gallery) return;

    const mediaSrcset = targetElement?.dataset?.mediaSrcset || targetElement?.closest?.('[data-media-srcset]')?.dataset?.mediaSrcset;
    const mediaSrc = targetElement?.dataset?.mediaSrc || targetElement?.closest?.('[data-media-src]')?.dataset?.mediaSrc;
    const isDesktop = window.innerWidth >= 750;

    if (isDesktop) {
      // 1. Desktop Grid Layout (.media-gallery__grid)
      const grid = gallery.querySelector('.media-gallery__grid');
      if (grid) {
        const firstGridItem = grid.querySelector('li:first-child');
        let targetGridItem = mediaId ? grid.querySelector(`li[data-media-id="${mediaId}"], [ref="media[]"][data-media-id="${mediaId}"]`) : null;
        if (!targetGridItem && mediaId) {
          const matchedEl = grid.querySelector(`[data-media-id="${mediaId}"]`);
          targetGridItem = matchedEl?.closest('li') || matchedEl;
        }

        if (targetGridItem && targetGridItem !== firstGridItem && !firstGridItem?.contains(targetGridItem)) {
          targetGridItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        } else if (mediaSrcset || mediaSrc) {
          const firstGridImg = firstGridItem?.querySelector('img');
          if (firstGridImg) {
            if (mediaSrcset) firstGridImg.srcset = mediaSrcset;
            if (mediaSrc) firstGridImg.src = mediaSrc;
          }
          if (firstGridItem && mediaId) {
            firstGridItem.setAttribute('data-media-id', mediaId);
            firstGridItem.querySelectorAll('[data-media-id]').forEach((el) => el.setAttribute('data-media-id', mediaId));
          }
          const zoomWrapper = firstGridItem?.querySelector('drag-zoom-wrapper');
          if (zoomWrapper && mediaSrc) {
            zoomWrapper.setAttribute('data-hd-src', mediaSrc);
          }
          firstGridItem?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    } else {
      // 2. Mobile Slideshow / Carousel
      const slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
      if (slides.length > 0 && mediaId) {
        const slideIndex = slides.findIndex((slide) => {
          return slide.dataset.mediaId === mediaId || slide.querySelector(`[data-media-id="${mediaId}"]`);
        });

        const activeIndex = slides.findIndex(
          (slide) => slide.getAttribute('aria-hidden') === 'false' || slide.classList.contains('slideshow__slide--active')
        );

        if (slideIndex !== -1 && slideIndex !== activeIndex && gallery.slideshow) {
          gallery.slideshow.select(slideIndex, undefined, { animate: true });
        } else if (mediaSrcset || mediaSrc) {
          const targetSlide = (slideIndex !== -1 ? slides[slideIndex] : null) || slides[activeIndex] || slides[0] || gallery;
          const targetImg = targetSlide.querySelector('img');
          if (targetImg) {
            if (mediaSrcset) targetImg.srcset = mediaSrcset;
            if (mediaSrc) targetImg.src = mediaSrc;
          }
          if (targetSlide && mediaId && targetSlide !== gallery) {
            targetSlide.setAttribute('data-media-id', mediaId);
            targetSlide.querySelectorAll('[data-media-id]').forEach((el) => el.setAttribute('data-media-id', mediaId));
          }
          if (slideIndex !== -1 && gallery.slideshow) {
            gallery.slideshow.select(slideIndex, undefined, { animate: true });
          }
        }
      } else if (mediaSrcset || mediaSrc) {
        const activeSlideImg = gallery.querySelector('slideshow-slide[aria-hidden="false"] img, .slideshow__slide--active img, .product-media__image, img');
        if (activeSlideImg) {
          if (mediaSrcset) activeSlideImg.srcset = mediaSrcset;
          if (mediaSrc) activeSlideImg.src = mediaSrc;
        }
      }
    }

    // Scroll up to gallery if the top of the gallery is cut off under the header or above viewport
    requestAnimationFrame(() => {
      const headerHeight =
        parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) ||
        document.querySelector('header, .header, #shopify-section-header')?.getBoundingClientRect().height ||
        70;
      const rect = gallery.getBoundingClientRect();
      const offset = 15;
      if (rect.top < headerHeight) {
        const targetY = window.scrollY + rect.top - headerHeight - offset;
        window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
      }
    });
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {object} [options] - Request options.
   * @param {string} [options.morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {{ optionValueId?: string, variantId?: string, connectedProductUrl?: string }} [options.detail] - Synchronous product select event detail.
   */
  fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {} } = {}) {
    const { optionValueId = '', variantId, connectedProductUrl = '', mediaId = '', isColorSelection = false } = detail;
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
          mediaId,
          isColorSelection,
        },
        promise: deferredEventPromise.promise,
      })
    );

    const handleResponseText = (responseText) => {
      this.#pendingRequestUrl = undefined;
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      // Defer is only useful for the initial rendering of the page. Remove it here.
      html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

      const variantPickerJsonScript = html.querySelector(`variant-picker script[data-selected-variant-json], variant-picker script[type="application/json"]:not(.size-guide-data)`);
      const textContent = variantPickerJsonScript?.textContent;

      if (!textContent) {
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
        return;
      }

      let newProduct;

      if (morphElementSelector === 'main') {
        this.updateMain(html);
      } else if (morphElementSelector) {
        this.updateElement(html, morphElementSelector);
      } else {
        const { overflowList } = this.refs;
        const wasSwatchesExpanded =
          overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

        newProduct = this.updateVariantPicker(html);

        if (wasSwatchesExpanded) {
          const overflowListAfterMorph = overflowList;
          if (overflowListAfterMorph instanceof OverflowList) {
            overflowListAfterMorph.showAll();
          }
        }
      }

      // Resolve the ProductSelectEvent promise with all data needed by listeners
      if (this.selectedOptionId) {
        const variantData = JSON.parse(textContent);

        if (variantData && typeof variantData === 'object') {
          const productViewAttr = variantPickerJsonScript
            ?.closest('[view-event-payload]')
            ?.getAttribute('view-event-payload')
            ?.trim();

          deferredEventPromise.resolve({
            variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
              sourceId: this.selectedOptionId,
              resource: variantData,
            },
          });

          return;
        }
      }

      // Variant data is null/invalid (e.g. unavailable variant combination) —
      // still include detail with html so listeners can update UI (disable buttons, morph text)
      deferredEventPromise.resolve({
        variant: null,
        detail: {
          html,
          productId: this.dataset.productId ?? '',
          newProduct,
          sourceId: this.selectedOptionId,
          resource: null,
        },
      });
    };

    if (VariantPicker.#sectionCache.has(requestUrl)) {
      const cachedText = VariantPicker.#sectionCache.get(requestUrl);
      if (cachedText) {
        handleResponseText(cachedText);
        return;
      }
    }

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        VariantPicker.#sectionCache.set(requestUrl, responseText);
        handleResponseText(responseText);
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    const preferred = localStorage.getItem('shopify_size_system');
    if (preferred) {
      this.applySizeSystem(preferred);
    }

    return newProduct;
  }

  /**
   * Initializes and listens for size system (EU/US/UK) changes.
   */
  initSizeSystemSwitchers() {
    const preferred = localStorage.getItem('shopify_size_system');
    if (preferred) {
      this.applySizeSystem(preferred);
    }

    this.addEventListener('change', (event) => {
      const switcher = event.target?.closest?.('[data-size-system-switcher]');
      if (switcher) {
        event.stopPropagation();
        const newSystem = switcher.value.toUpperCase();
        try {
          localStorage.setItem('shopify_size_system', newSystem);
        } catch (e) {}
        this.applySizeSystem(newSystem);
        window.dispatchEvent(new CustomEvent('size-system-changed', { detail: { system: newSystem } }));
      }
    });
  }

  /**
   * Dynamically applies the chosen size system across all buttons, dropdowns, and legends.
   * @param {string} system - 'EU', 'US', or 'UK'
   */
  applySizeSystem(system) {
    if (!system) return;
    const sys = system.toUpperCase();
    const sysLower = sys.toLowerCase();

    // Sync all size system switchers
    this.querySelectorAll('[data-size-system-switcher]').forEach((select) => {
      select.value = sys;
    });

    // Update all button labels in fieldsets with multi-system sizes
    const fieldsets = this.querySelectorAll('fieldset.variant-option');
    fieldsets.forEach((fieldset) => {
      const labels = fieldset.querySelectorAll('.variant-option__button-label');
      labels.forEach((label) => {
        const textEl = label.querySelector('[data-variant-size-text]');
        const targetValue = label.getAttribute(`data-size-${sysLower}`);
        if (textEl && targetValue) {
          textEl.textContent = targetValue;
        }
      });

      // Update selected value in legend
      const checkedInput = fieldset.querySelector('input:checked');
      const checkedLabel = checkedInput?.closest('.variant-option__button-label');
      const legendVal = fieldset.querySelector('.variant-option__selected-value');
      if (legendVal) {
        const targetSelectedValue = checkedLabel?.getAttribute(`data-size-${sysLower}`) || legendVal.getAttribute(`data-size-${sysLower}`);
        if (targetSelectedValue) {
          legendVal.textContent = targetSelectedValue;
        }
      }
    });

    // Update single option containers
    this.querySelectorAll('.variant-option[data-testid="variant-option-single"]').forEach((single) => {
      const legendVal = single.querySelector('.variant-option__selected-value');
      if (legendVal) {
        const targetSelectedValue = legendVal.getAttribute(`data-size-${sysLower}`);
        if (targetSelectedValue) {
          legendVal.textContent = targetSelectedValue;
        }
      }
    });

    // Update dropdown options if present
    this.querySelectorAll('.variant-option__select option').forEach((opt) => {
      const targetValue = opt.getAttribute(`data-size-${sysLower}`);
      if (targetValue) {
        opt.textContent = targetValue;
      }
    });
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select.variant-option__select option[selected], select:not([data-size-system-switcher]) option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select.variant-option__select, select:not([data-size-system-switcher])')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    // For radio/checkbox fieldsets, :checked reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select.variant-option__select option[selected], select:not([data-size-system-switcher]) option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
